// EnhancedPersonnelCreation.tsx - Updated for new database schema and backend APIs
// UPDATED: Restyled fields to match OrganizationForm look and feel (bottom-border style)
// NOTE: This component renders INSIDE PersonnelManagement.tsx's modal shell.
//       It must NOT render its own help panel, toggle strip, or modal-content-with-help wrapper.
import React, { useState, useEffect } from 'react';
import './PersonnelFormOverrides.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

interface ExtractedPersonnelData {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  sex?: string;
  dateOfBirth?: string;
  placeOfBirth?: string;
  nationality?: string;
  nationalIdNo?: string;
  passportNumber?: string;
  passportIssueDate?: string;
  passportExpiryDate?: string;
  typeOfVisa?: string;
  visaIssueDate?: string;
  visaExpiry?: string;
  bloodGroup?: string;
  confidence?: number;
  photoData?: {
    photoDetected: boolean;
    photoCount: number;
    extractedPhotos?: Array<{
      id: string;
      buffer?: ArrayBuffer;
      mimeType: string;
      width: number;
      height: number;
      confidence: number;
      description: string;
    }>;
    photoLocations?: Array<{
      id: string;
      polygon: any;
      confidence: number;
      description: string;
    }>;
    extractionMethod: string;
    note?: string;
    error?: string;
  };
  extractionDetails?: {
    documentType: string;
    processingMethod: string;
    fieldsExtracted: string[];
    sourceDocuments: string[];
    prioritySource?: string;
    photoExtracted?: boolean;
    photoCount?: number;
    fieldConfidences?: Record<string, number>;
  };
  [key: string]: any;
}

interface DocumentUpload {
  type: 'passport_front' | 'passport_back' | 'visa_national_id' | 'profile_picture';
  file: File | null;
  label: string;
  description: string;
  icon: string;
  required: boolean;
  uploaded: boolean;
  extractedData?: ExtractedPersonnelData;
  processingStatus: 'pending' | 'processing' | 'completed' | 'error';
  errorMessage?: string;
}

interface EnhancedPersonnelCreationProps {
  onPersonnelCreated: (data: any) => void;
  onCancel: () => void;
  getAccessToken: () => Promise<string>;
  onStatusUpdate?: (status: { type: string; message: string }) => void;
  onStepChange?: (step: 'upload' | 'processing' | 'form' | 'saving') => void;
}

const addSpinnerCSS = () => {
  const styleId = 'spinner-css';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .spinner {
        width: 24px;
        height: 24px;
        border: 2.5px solid #f3f3f3;
        border-top: 2.5px solid #F3694A;
        border-radius: 50%;
        display: inline-block;
        animation: spin 1s linear infinite;
      }
    `;
    document.head.appendChild(style);
  }
};

const addUnfilledFieldCSS = () => {
  const styleId = 'pf-unfilled-css';
  // Always replace so hot-reload picks up changes
  const existing = document.getElementById(styleId);
  if (existing) existing.remove();

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    /* ── Unfilled field highlight ── */
    .pf-unfilled-field {
      border-bottom-color: #F3694A !important;
      background-color: #FEF6F4 !important;
      border-bottom-width: 2px !important;
      transition: background-color 0.25s ease, border-bottom-color 0.25s ease !important;
    }
    select.pf-unfilled-field {
      border-color: #F3694A !important;
      background-color: #FEF6F4 !important;
    }
    .pf-unfilled-field:focus {
      background-color: #ffffff !important;
      border-bottom-color: #F3694A !important;
      box-shadow: 0 2px 0 0 #F3694A !important;
    }
    select.pf-unfilled-field:focus {
      background-color: #ffffff !important;
      border-color: #F3694A !important;
      box-shadow: 0 0 0 2px rgba(243,105,74,0.15) !important;
    }

    /* Label badge shown next to unfilled field labels */
    .pf-unfilled-badge {
      display: inline-flex !important;
      align-items: center !important;
      gap: 3px !important;
      margin-left: 6px !important;
      padding: 1px 7px !important;
      border-radius: 3px !important;
      font-size: 10px !important;
      font-weight: 600 !important;
      background-color: #FEF6F4 !important;
      color: #F3694A !important;
      border: 1px solid #FDDDD6 !important;
      font-family: 'Segoe UI', sans-serif !important;
      letter-spacing: 0.01em !important;
      vertical-align: middle !important;
      white-space: nowrap !important;
    }
  `;
  document.head.appendChild(style);
};

export const EnhancedPersonnelCreation: React.FC<EnhancedPersonnelCreationProps> = ({
  onPersonnelCreated,
  onCancel,
  getAccessToken,
  onStatusUpdate,
  onStepChange
}) => {
  useEffect(() => {
    addSpinnerCSS();
    addUnfilledFieldCSS();
  }, []);

  const [step, setStep] = useState<'upload' | 'processing' | 'form' | 'saving'>('upload');
  const [isProcessing, setIsProcessing] = useState(false);
  const [combinedData, setCombinedData] = useState<ExtractedPersonnelData | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showPhotoComparison, setShowPhotoComparison] = useState(false);
  const [extractedPhotos, setExtractedPhotos] = useState<any[]>([]);
  const [crossDocWarnings, setCrossDocWarnings] = useState<Array<{ field: string; label: string; passportValue: string; idValue: string }>>([]);
  const [warningsDismissed, setWarningsDismissed] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Tracks fields that were genuinely extracted with usable values at form initialization
  const [genuinelyExtractedFields, setGenuinelyExtractedFields] = useState<Set<string>>(new Set());
  const [documentAlerts, setDocumentAlerts] = useState<Array<{
    field: string;
    severity: 'expired' | 'expiring_soon' | 'expiring_later';
    label: string;
    message: string;
    daysRemaining: number;
  }>>([]);

  // ENHANCED STEP SETTER WITH TRACKING
  const updateStep = (newStep: 'upload' | 'processing' | 'form' | 'saving') => {
    setStep(newStep);
    if (onStepChange) {
      onStepChange(newStep);
    }
  };

  // Notify parent of initial step on mount
  useEffect(() => {
    if (onStepChange) {
      onStepChange('upload');
    }
  }, [onStepChange]);

  const [documentUploads, setDocumentUploads] = useState<DocumentUpload[]>([
    {
      type: 'passport_front',
      file: null,
      label: 'Passport',
      description: 'Upload front page of passport',
      icon: '📖',
      required: false,
      uploaded: false,
      processingStatus: 'pending'
    },
    {
      type: 'visa_national_id',
      file: null,
      label: 'Visa/ID',
      description: 'Visa or National ID',
      icon: '🆔',
      required: false,
      uploaded: false,
      processingStatus: 'pending'
    },
    {
      type: 'profile_picture',
      file: null,
      label: 'Photo',
      description: 'Profile picture',
      icon: '📷',
      required: false,
      uploaded: false,
      processingStatus: 'pending'
    }
  ]);

  const updateMessage = (newMessage: { type: 'success' | 'error' | 'info' | 'warning'; text: string }) => {
    if (onStatusUpdate) {
      onStatusUpdate({ type: newMessage.type, message: newMessage.text });
    }
  };

  const handleFileUpload = (documentType: DocumentUpload['type'], file: File) => {
    const allowedTypes = documentType === 'profile_picture' 
      ? ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
      : ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    
    if (!allowedTypes.includes(file.type)) {
      updateMessage({ 
        type: 'error', 
        text: documentType === 'profile_picture' 
          ? 'Profile picture must be an image file (JPEG, PNG, GIF)'
          : 'Documents must be PDF or image files (JPEG, PNG, GIF)'
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      updateMessage({ type: 'error', text: 'File size must be less than 10MB' });
      return;
    }

    setDocumentUploads(prev => prev.map(upload => 
      upload.type === documentType 
        ? { ...upload, file, uploaded: true, processingStatus: 'pending' as const, errorMessage: undefined }
        : upload
    ));

    updateMessage({ 
      type: 'success', 
      text: `✅ ${documentUploads.find(d => d.type === documentType)?.label} uploaded successfully` 
    });
  };

  const processDocument = async (upload: DocumentUpload): Promise<ExtractedPersonnelData | null> => {
    if (!upload.file || upload.type === 'profile_picture') {
      return null;
    }

    const formData = new FormData();
    formData.append('document', upload.file);
    
    let documentType = 'identity';
    if (upload.type === 'passport_front' || upload.type === 'passport_back') {
      documentType = 'passport';
    } else if (upload.type === 'visa_national_id') {
      documentType = 'visa_id';
    }
    
    formData.append('documentType', documentType);

    const accessToken = await getAccessToken();
    const response = await fetch(`${API_BASE_URL}/personnel/process-document`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${accessToken}` },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Processing failed');
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Processing failed');
    }

    return result.data as ExtractedPersonnelData;
  };

  const PhotoComparisonModal = () => {
    if (!showPhotoComparison) return null;

    const profilePicture = documentUploads.find(u => u.type === 'profile_picture' && u.file);
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1200
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          maxWidth: '900px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        }}>
          <div style={{
            padding: '24px',
            borderBottom: '1px solid #dee2e6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{ margin: 0, color: '#333' }}>📷 Photo Verification</h2>
            <button
              onClick={() => setShowPhotoComparison(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '4px'
              }}
            >
              ✕
            </button>
          </div>

          <div style={{ padding: '24px', overflowY: 'auto', maxHeight: 'calc(80vh - 100px)' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '32px',
              marginBottom: '24px'
            }}>
              
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ marginBottom: '16px', color: '#28a745' }}>📤 Uploaded Profile Picture</h3>
                <div style={{
                  width: '250px',
                  height: '300px',
                  margin: '0 auto',
                  border: '3px solid #28a745',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f8f9fa'
                }}>
                  {profilePicture?.file ? (
                    <img 
                      src={URL.createObjectURL(profilePicture.file)} 
                      alt="Uploaded Profile"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', color: '#6c757d' }}>
                      <div style={{ fontSize: '48px', marginBottom: '8px' }}>👤</div>
                      <p>No profile picture uploaded</p>
                    </div>
                  )}
                </div>
                <div style={{
                  marginTop: '12px',
                  padding: '8px 12px',
                  backgroundColor: '#d4edda',
                  color: '#155724',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}>
                  Manually uploaded by user
                </div>
              </div>

              <div style={{ textAlign: 'center' }}>
                <h3 style={{ marginBottom: '16px', color: '#007bff' }}>🤖 AI-Extracted Photos</h3>
                <div style={{
                  width: '250px',
                  height: '300px',
                  margin: '0 auto',
                  border: '3px solid #007bff',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f8f9fa'
                }}>
                  {extractedPhotos.length > 0 ? (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '48px', marginBottom: '8px' }}>📸</div>
                      <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
                        {extractedPhotos.length} photo{extractedPhotos.length > 1 ? 's' : ''} detected
                      </p>
                      <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                        From passport/ID document
                      </p>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', color: '#6c757d' }}>
                      <div style={{ fontSize: '48px', marginBottom: '8px' }}>🔍</div>
                      <p>No photos extracted from documents</p>
                    </div>
                  )}
                </div>
                <div style={{
                  marginTop: '12px',
                  padding: '8px 12px',
                  backgroundColor: extractedPhotos.length > 0 ? '#d1ecf1' : '#fff3cd',
                  color: extractedPhotos.length > 0 ? '#0c5460' : '#856404',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}>
                  {extractedPhotos.length > 0 
                    ? `AI-extracted from ${extractedPhotos.length} source${extractedPhotos.length > 1 ? 's' : ''}`
                    : 'AI photo extraction in progress'
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Cross-document validation ──────────────────────────────────────
  // Compares overlapping fields between passport and visa/ID extractions.
  // Produces warnings (not blocking errors) for the user to review.
  const performCrossDocumentValidation = (
    allExtractedData: ExtractedPersonnelData[],
    sourceDocuments: string[]
  ): Array<{ field: string; label: string; passportValue: string; idValue: string }> => {
    const warnings: Array<{ field: string; label: string; passportValue: string; idValue: string }> = [];

    // Find passport data vs visa/ID data
    const passportIdx = sourceDocuments.findIndex(s => s.toLowerCase().includes('passport'));
    const idIdx = sourceDocuments.findIndex(s =>
      s.toLowerCase().includes('visa') || s.toLowerCase().includes('id')
    );

    if (passportIdx === -1 || idIdx === -1) return warnings; // Only one doc — nothing to compare

    const passportData = allExtractedData[passportIdx];
    const idData = allExtractedData[idIdx];
    if (!passportData || !idData) return warnings;

    // Helper: normalise strings for comparison (trim, lowercase, collapse spaces)
    const norm = (v: any) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');

    // Fields to compare across documents
    const fieldsToCompare: Array<{ key: string; label: string; altKeys?: string[] }> = [
      { key: 'fullName', label: 'Full Name', altKeys: ['firstName', 'lastName'] },
      { key: 'firstName', label: 'First Name' },
      { key: 'lastName', label: 'Last Name / Surname' },
      { key: 'dateOfBirth', label: 'Date of Birth' },
      { key: 'sex', label: 'Gender' },
      { key: 'nationality', label: 'Nationality' },
    ];

    for (const { key, label } of fieldsToCompare) {
      const pVal = passportData[key];
      const iVal = idData[key];

      // Only compare if both documents extracted this field
      if (!pVal || !iVal) continue;
      if (norm(pVal) === '' || norm(iVal) === '') continue;

      if (norm(pVal) !== norm(iVal)) {
        // For names: do a more forgiving check (one could be substring of other)
        if (key === 'fullName' || key === 'firstName' || key === 'lastName') {
          const pNorm = norm(pVal);
          const iNorm = norm(iVal);
          // If one contains the other, skip (e.g. "JOHN" vs "JOHN JAMES")
          if (pNorm.includes(iNorm) || iNorm.includes(pNorm)) continue;
        }

        warnings.push({
          field: key,
          label,
          passportValue: String(pVal).trim(),
          idValue: String(iVal).trim()
        });
      }
    }

    return warnings;
  };

  const processAllDocuments = async () => {
    const uploadedDocuments = documentUploads.filter(upload => upload.uploaded);
    
    if (uploadedDocuments.length === 0) {
      updateMessage({ type: 'error', text: 'Please upload at least one document' });
      return;
    }

    updateStep('processing');
    setIsProcessing(true);
    updateMessage({ type: 'info', text: 'Processing documents with AI...' });

    try {
      const allExtractedData: ExtractedPersonnelData[] = [];
      const sourceDocuments: string[] = [];
      let allExtractedPhotos: any[] = [];
      
      for (const upload of uploadedDocuments) {
        if (upload.type === 'profile_picture') continue;

        setDocumentUploads(prev => prev.map(u => 
          u.type === upload.type 
            ? { ...u, processingStatus: 'processing' as const }
            : u
        ));

        updateMessage({ type: 'info', text: `Processing ${upload.label}...` });

        try {
          const extractedData = await processDocument(upload);
          if (extractedData) {
            allExtractedData.push(extractedData);
            sourceDocuments.push(upload.label);
            
            if (extractedData.photoData?.photoDetected && extractedData.photoData.extractedPhotos) {
              allExtractedPhotos = [...allExtractedPhotos, ...extractedData.photoData.extractedPhotos];
            }
            
            setDocumentUploads(prev => prev.map(u => 
              u.type === upload.type 
                ? { ...u, processingStatus: 'completed' as const, extractedData }
                : u
            ));

            const photoMessage = extractedData.photoData?.photoDetected 
              ? ` + ${extractedData.photoData.photoCount} photo(s)` 
              : '';

            updateMessage({ 
              type: 'success', 
              text: `✅ ${upload.label} processed - extracted ${extractedData.extractionDetails?.fieldsExtracted?.length || 0} fields${photoMessage}` 
            });
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Processing failed';
          
          setDocumentUploads(prev => prev.map(u => 
            u.type === upload.type 
              ? { ...u, processingStatus: 'error' as const, errorMessage }
              : u
          ));

          updateMessage({ type: 'warning', text: `⚠️ Failed to process ${upload.label}: ${errorMessage}` });
        }

        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      setExtractedPhotos(allExtractedPhotos);

      const combinedExtractedData = combineExtractedData(allExtractedData, sourceDocuments);
      setCombinedData(combinedExtractedData);
      
      const initialFormData: Record<string, string> = {};
      Object.keys(combinedExtractedData).forEach(key => {
        if (key !== 'confidence' && key !== 'extractionDetails' && key !== 'photoData') {
          const value = combinedExtractedData[key];
          initialFormData[key] = value ? String(value) : '';
        }
      });

      // Clean up select fields: if extracted value doesn't match a valid option, clear it
      const selectValidOptions: Record<string, string[]> = {
        sex: ['Male', 'Female'],
        bloodGroup: ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'],
        typeOfVisa: ['Tourist', 'Business', 'Work', 'Student', 'Transit', 'Resident', 'Other'],
      };
      Object.entries(selectValidOptions).forEach(([key, validValues]) => {
        if (initialFormData[key] && !validValues.includes(initialFormData[key])) {
          initialFormData[key] = '';
        }
      });
      
      if (!initialFormData.fullName && initialFormData.firstName && initialFormData.lastName) {
        initialFormData.fullName = `${initialFormData.firstName} ${initialFormData.lastName}`;
      }
      // Reverse-split: if we have fullName but not firstName/lastName, derive them
      if (initialFormData.fullName && (!initialFormData.firstName || !initialFormData.lastName)) {
        const nameParts = initialFormData.fullName.trim().split(/\s+/);
        if (nameParts.length >= 2) {
          initialFormData.firstName = initialFormData.firstName || nameParts[0];
          initialFormData.lastName = initialFormData.lastName || nameParts.slice(1).join(' ');
        } else if (nameParts.length === 1) {
          initialFormData.firstName = initialFormData.firstName || nameParts[0];
        }
      }

      // Snapshot which fields were genuinely extracted with usable values
      const extractedFieldList = combinedExtractedData.extractionDetails?.fieldsExtracted || [];
      const genuine = new Set<string>();
      extractedFieldList.forEach((key: string) => {
        const value = initialFormData[key];
        if (value && value.trim() !== '') {
          genuine.add(key);
        }
      });
      // If fullName was extracted and we derived firstName/lastName, mark them as extracted too
      if (genuine.has('fullName') || genuine.has('firstName') || genuine.has('lastName')) {
        if (initialFormData.firstName) genuine.add('firstName');
        if (initialFormData.lastName) genuine.add('lastName');
      }
      setGenuinelyExtractedFields(genuine);
      
      setFormData(initialFormData);
      
      // ── Cross-document mismatch detection ──
      const warnings = performCrossDocumentValidation(allExtractedData, sourceDocuments);
      setCrossDocWarnings(warnings);
      setWarningsDismissed(false);

      updateStep('form');

      const photoSummary = allExtractedPhotos.length > 0 
        ? ` Photos: ${allExtractedPhotos.length} extracted for verification.`
        : '';

      updateMessage({ 
        type: 'success', 
        text: `🎉 Documents processed! Extracted ${combinedExtractedData.extractionDetails?.fieldsExtracted?.length || 0} fields.${photoSummary} Please review and complete the form below.` 
      });

    } catch (error) {
      updateMessage({ 
        type: 'error', 
        text: `Processing failed: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
      updateStep('upload');
    } finally {
      setIsProcessing(false);
    }
  };

  // ── Confidence-aware smart merge ──────────────────────────────────
  // Priority rules:
  //   1. Per-field: pick the value with highest confidence (from fieldConfidences)
  //   2. Visa/ID card wins for: nationality, visaExpiry, visaIssueDate, typeOfVisa, nationalIdNo
  //   3. Passport wins for: passportNumber, passportIssueDate, passportExpiryDate
  //   4. For shared fields (name, DOB, sex): highest confidence wins
  const combineExtractedData = (dataArray: ExtractedPersonnelData[], sourceDocuments: string[]): ExtractedPersonnelData => {
    const combined: ExtractedPersonnelData = {};
    const allFieldsExtracted = new Set<string>();
    const fieldConfidenceMap: Record<string, number> = {};
    let totalConfidence = 0;
    let confidenceCount = 0;
    let totalPhotosDetected = 0;
    let photoExtractionMethods: string[] = [];

    // Fields where visa/ID card data should take priority
    const visaPriorityFields = new Set([
      'nationality', 'visaExpiry', 'visaIssueDate', 'typeOfVisa', 'nationalIdNo'
    ]);
    // Fields where passport data should take priority
    const passportPriorityFields = new Set([
      'passportNumber', 'passportIssueDate', 'passportExpiryDate'
    ]);

    // Identify which data came from which document type
    const isVisaIdDoc = (index: number) => {
      const label = sourceDocuments[index]?.toLowerCase() || '';
      return label.includes('visa') || label.includes('id');
    };

    // Merge all data with confidence-aware overwriting
    dataArray.forEach((data, docIndex) => {
      const docFieldConfidences = data.extractionDetails?.fieldConfidences as Record<string, number> || {};
      const isVisa = isVisaIdDoc(docIndex);

      Object.entries(data).forEach(([key, value]) => {
        if (['confidence', 'extractionDetails', 'photoData'].includes(key)) return;
        if (!value || String(value).trim() === '') return;

        const existingConf = fieldConfidenceMap[key] || 0;
        const newConf = docFieldConfidences[key] || (data.confidence || 0.5);

        // Determine if this source should take priority for this field
        let hasPriority = false;
        if (isVisa && visaPriorityFields.has(key)) hasPriority = true;
        if (!isVisa && passportPriorityFields.has(key)) hasPriority = true;

        // Overwrite if: field is empty, new confidence is higher, or has document-type priority
        if (!combined[key] || hasPriority || newConf > existingConf) {
          combined[key] = value;
          fieldConfidenceMap[key] = hasPriority ? Math.max(newConf, 0.9) : newConf;
          allFieldsExtracted.add(key);
        }
      });

      if (data.confidence) {
        totalConfidence += data.confidence;
        confidenceCount++;
      }

      if (data.photoData?.photoDetected) {
        totalPhotosDetected += data.photoData.photoCount || 0;
      }
      if (data.photoData?.extractionMethod && !photoExtractionMethods.includes(data.photoData.extractionMethod)) {
        photoExtractionMethods.push(data.photoData.extractionMethod);
      }

      if (data.extractionDetails?.fieldsExtracted) {
        data.extractionDetails.fieldsExtracted.forEach((field: string) => allFieldsExtracted.add(field));
      }
    });

    // Construct full name if parts exist
    if (combined.firstName && combined.lastName && !combined.fullName) {
      combined.fullName = `${combined.firstName} ${combined.lastName}`;
      allFieldsExtracted.add('fullName');
    }

    const averageConfidence = confidenceCount > 0 ? totalConfidence / confidenceCount : 0.5;
    combined.confidence = averageConfidence;

    combined.photoData = {
      photoDetected: totalPhotosDetected > 0,
      photoCount: totalPhotosDetected,
      extractionMethod: photoExtractionMethods.join(', ') || 'None',
      note: totalPhotosDetected > 0
        ? `${totalPhotosDetected} photos detected across ${sourceDocuments.length} documents`
        : 'No photos detected in uploaded documents'
    };

    combined.extractionDetails = {
      documentType: 'multi-document',
      processingMethod: 'Multi-Document AI Processing (confidence-aware merge)',
      fieldsExtracted: Array.from(allFieldsExtracted),
      sourceDocuments,
      prioritySource: 'Per-field confidence + document-type priority',
      photoExtracted: totalPhotosDetected > 0,
      photoCount: totalPhotosDetected
    };

    return combined;
  };

  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }

    // Clear save error banner when editing fields that may have caused a duplicate error
    if ((field === 'passportNumber' || field === 'nationalIdNo') && saveError) {
      setSaveError(null);
    }
    
    if (field === 'firstName' || field === 'lastName') {
      const firstName = field === 'firstName' ? value : formData.firstName || '';
      const lastName = field === 'lastName' ? value : formData.lastName || '';
      if (firstName && lastName) {
        setFormData(prev => ({ ...prev, fullName: `${firstName} ${lastName}` }));
      }
    }
  };

  // ── Document expiry checks ──────────────────────────────────────
  // Re-runs whenever relevant date fields in formData change.
  const computeDocumentAlerts = (data: Record<string, string>) => {
    const alerts: typeof documentAlerts = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const checks: Array<{ field: string; label: string; dateValue: string }> = [
      { field: 'passportExpiryDate', label: 'Passport', dateValue: data.passportExpiryDate || '' },
      { field: 'visaExpiry', label: 'Visa', dateValue: data.visaExpiry || '' },
    ];

    for (const { field, label, dateValue } of checks) {
      if (!dateValue) continue;
      const expiry = new Date(dateValue);
      if (isNaN(expiry.getTime())) continue;
      expiry.setHours(0, 0, 0, 0);

      const diffMs = expiry.getTime() - today.getTime();
      const daysRemaining = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

      if (daysRemaining < 0) {
        const daysAgo = Math.abs(daysRemaining);
        alerts.push({
          field,
          severity: 'expired',
          label,
          message: `${label} expired ${daysAgo === 1 ? 'yesterday' : `${daysAgo} days ago`} (${dateValue})`,
          daysRemaining,
        });
      } else if (daysRemaining <= 30) {
        alerts.push({
          field,
          severity: 'expiring_soon',
          label,
          message: `${label} expires in ${daysRemaining === 0 ? 'today' : daysRemaining === 1 ? '1 day' : `${daysRemaining} days`} (${dateValue})`,
          daysRemaining,
        });
      } else if (daysRemaining <= 90) {
        alerts.push({
          field,
          severity: 'expiring_later',
          label,
          message: `${label} expires in ${daysRemaining} days (${dateValue})`,
          daysRemaining,
        });
      }
    }

    return alerts;
  };

  // Re-compute alerts when passport/visa expiry dates change
  useEffect(() => {
    if (step === 'form') {
      setDocumentAlerts(computeDocumentAlerts(formData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.passportExpiryDate, formData.visaExpiry, step]);

  // ── Mandatory fields — saving is blocked if any are empty ──
  const MANDATORY_FIELDS: { key: string; label: string }[] = [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'sex', label: 'Gender' },
    { key: 'dateOfBirth', label: 'Date of Birth' },
    { key: 'placeOfBirth', label: 'Place of Birth' },
    { key: 'nationality', label: 'Nationality' },
    { key: 'nationalIdNo', label: 'National ID Number' },
    { key: 'passportNumber', label: 'Passport Number' },
    { key: 'passportIssueDate', label: 'Passport Issue Date' },
    { key: 'passportExpiryDate', label: 'Passport Expiry Date' },
    { key: 'typeOfVisa', label: 'Visa Type' },
    { key: 'visaIssueDate', label: 'Visa Issue Date' },
    { key: 'visaExpiry', label: 'Visa Expiry Date' },
  ];

  const validateForm = (): Record<string, string> | null => {
    const errors: Record<string, string> = {};

    MANDATORY_FIELDS.forEach(({ key, label }) => {
      const value = formData[key];
      if (!value || value.trim() === '') {
        errors[key] = `${label} is required`;
      }
    });

    // Ensure fullName is present (auto-derive if first+last are filled)
    if (!errors['firstName'] && !errors['lastName']) {
      const first = (formData.firstName || '').trim();
      const last = (formData.lastName || '').trim();
      if (first && last && (!formData.fullName || formData.fullName.trim() === '')) {
        setFormData(prev => ({ ...prev, fullName: `${first} ${last}` }));
      }
    }
    if (!formData.fullName || formData.fullName.trim() === '') {
      errors['fullName'] = 'Full Name is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length > 0 ? errors : null;
  };

  // ── Duplicate passport check (same company) ───────────────────────
  const checkDuplicatePassport = async (passportNumber: string): Promise<{ isDuplicate: boolean; existingName?: string }> => {
    try {
      const accessToken = await getAccessToken();
      const response = await fetch(
        `${API_BASE_URL}/personnel/check-duplicate?passportNumber=${encodeURIComponent(passportNumber)}`,
        {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${accessToken}` },
        }
      );
      if (response.ok) {
        const result = await response.json();
        return { isDuplicate: result.isDuplicate === true, existingName: result.existingName };
      }
      // If the endpoint doesn't exist yet (404) or errors, allow save to proceed
      // — the backend 409 will still catch it as a fallback
      return { isDuplicate: false };
    } catch {
      return { isDuplicate: false };
    }
  };

  const savePersonnelRecord = async () => {
    const errors = validateForm();
    if (errors) {
      const missingCount = Object.keys(errors).length;
      const missingNames = Object.values(errors).slice(0, 3).map(v => v.replace(' is required', '')).join(', ');
      const suffix = missingCount > 3 ? ` and ${missingCount - 3} more` : '';
      updateMessage({ type: 'error', text: `Please fill in all required fields: ${missingNames}${suffix}` });

      // Scroll first error into view
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        setTimeout(() => {
          const el = document.querySelector(`[data-field="${firstErrorKey}"]`) as HTMLElement;
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
      }
      return;
    }

    // ── Pre-save duplicate passport check (within the same company) ──
    const passportNum = (formData.passportNumber || '').trim();
    if (passportNum) {
      const dupCheck = await checkDuplicatePassport(passportNum);
      if (dupCheck.isDuplicate) {
        const existingInfo = dupCheck.existingName
          ? ` (existing record: ${dupCheck.existingName})`
          : '';
        const dupError = `A personnel record with passport number "${passportNum}" already exists in your company${existingInfo}. Please verify the passport number or update the existing record instead.`;
        setValidationErrors(prev => ({ ...prev, passportNumber: `Passport number already in use${existingInfo}` }));
        setSaveError(dupError);
        updateMessage({ type: 'error', text: dupError });

        // Scroll to top of form so the error banner is visible
        setTimeout(() => {
          const modalBody = document.querySelector('.modal-body');
          modalBody?.scrollTo({ top: 0, behavior: 'smooth' });
        }, 50);
        return;
      }
    }

    setSaveError(null);
    updateStep('saving');
    updateMessage({ type: 'info', text: 'Creating personnel record...' });

    try {
      const accessToken = await getAccessToken();
      const submitFormData = new FormData();

      // Send ALL form fields — including empty ones so the backend
      // doesn't fall back to stale AI-extracted data for cleared fields
      Object.entries(formData).forEach(([key, value]) => {
        submitFormData.append(key, (value || '').trim());
      });

      const profilePicture = documentUploads.find(u => u.type === 'profile_picture' && u.file);
      const passportDoc = documentUploads.find(u => u.type === 'passport_front' && u.file);
      const visaIdDoc = documentUploads.find(u => u.type === 'visa_national_id' && u.file);

      if (profilePicture?.file) {
        submitFormData.append('photoFile', profilePicture.file);
      }

      if (passportDoc?.file) {
        submitFormData.append('passportDocument', passportDoc.file);
        submitFormData.append('passportDocumentName', passportDoc.file.name);
        submitFormData.append('passportDocumentType', passportDoc.file.type);
        submitFormData.append('passportDocumentSize', passportDoc.file.size.toString());
      }

      if (visaIdDoc?.file) {
        submitFormData.append('visaIdDocument', visaIdDoc.file);
        submitFormData.append('visaIdDocumentName', visaIdDoc.file.name);
        submitFormData.append('visaIdDocumentType', visaIdDoc.file.type);
        submitFormData.append('visaIdDocumentSize', visaIdDoc.file.size.toString());
      }

      if (combinedData?.extractionDetails) {
        submitFormData.append('processingMetadata', JSON.stringify(combinedData.extractionDetails));
      }

      const response = await fetch(`${API_BASE_URL}/personnelcreate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
        body: submitFormData
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const uploadSummary = [];
          if (result.data.photoUploaded) uploadSummary.push('profile photo');
          if (result.data.passportDocumentUploaded) uploadSummary.push('passport document');
          if (result.data.visaIdDocumentUploaded) uploadSummary.push('visa/ID document');
          
          const documentSummary = uploadSummary.length > 0 
            ? ` Documents uploaded: ${uploadSummary.join(', ')}.`
            : ' No documents were uploaded.';

          updateMessage({ type: 'success', text: `✅ Personnel record created successfully!${documentSummary}` });
          setTimeout(() => onPersonnelCreated(result.data), 1500);
        } else {
          throw new Error(result.error || 'Failed to create record');
        }
      } else {
        // Parse the error response — this is where 409 duplicates come through
        let errorMessage = 'Failed to create record';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (_) {
          errorMessage = `Server responded with status ${response.status}`;
        }

        // Provide a user-friendly message for known error codes
        if (response.status === 409) {
          // Determine which field triggered the duplicate based on backend response
          const isDuplicatePassport = errorMessage.toLowerCase().includes('passport');
          const fieldLabel = isDuplicatePassport ? 'passport number' : 'National ID';
          const fieldValue = isDuplicatePassport ? (formData.passportNumber || '') : (formData.nationalIdNo || '');
          const fieldKey = isDuplicatePassport ? 'passportNumber' : 'nationalIdNo';
          errorMessage = `A personnel record with ${fieldLabel} "${fieldValue}" already exists in your company. Please check the ${fieldLabel} or update the existing record instead.`;
          setValidationErrors(prev => ({ ...prev, [fieldKey]: `${isDuplicatePassport ? 'Passport number' : 'National ID'} already in use` }));
        } else if (response.status === 400) {
          errorMessage = `Validation error: ${errorMessage}`;
        } else if (response.status === 401 || response.status === 403) {
          errorMessage = 'You do not have permission to create personnel records. Please contact your administrator.';
        }

        throw new Error(errorMessage);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      updateStep('form');
      setSaveError(message);
      updateMessage({ type: 'error', text: `Save failed: ${message}` });
      // Scroll to top of form so the error banner is visible
      setTimeout(() => {
        const modalBody = document.querySelector('.modal-body');
        modalBody?.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
      console.error('Personnel save error:', error);
    }
  };

  const getUploadedCount = () => {
    return documentUploads.filter(doc => doc.uploaded).length;
  };

  // Valid option sets for select fields — extracted values that don't match are treated as unfilled
  const VALID_SELECT_OPTIONS: Record<string, string[]> = {
    sex: ['Male', 'Female'],
    bloodGroup: ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'],
    typeOfVisa: ['Tourist', 'Business', 'Work', 'Student', 'Transit', 'Resident', 'Other'],
  };

  // Helper: check if a field has a usable value
  // For select fields, the value must match a valid option; for text/date, non-empty is enough
  const hasUsableValue = (fieldKey: string): boolean => {
    const value = formData[fieldKey];
    if (!value || value.trim() === '') return false;
    const validOptions = VALID_SELECT_OPTIONS[fieldKey];
    if (validOptions) return validOptions.includes(value);
    return true;
  };

  // Helper: check if a field was genuinely extracted with a usable value at form init time
  // This does NOT flip to true when the user manually fills a field later
  const isExtracted = (fieldKey: string): boolean => {
    return genuinelyExtractedFields.has(fieldKey);
  };

  // Helper: check if a field is unfilled (no usable value)
  const isUnfilled = (fieldKey: string): boolean => {
    return !hasUsableValue(fieldKey);
  };

  // Helper: build className for form inputs/selects
  // - Adds 'pf-extracted-value' if extracted and has a usable value
  // - Adds 'pf-unfilled-field' if no usable value
  const fieldClass = (fieldKey: string, base: string = 'field-input'): string => {
    const classes = [base];
    if (isExtracted(fieldKey)) {
      classes.push('pf-extracted-value');
    }
    if (isUnfilled(fieldKey)) {
      classes.push('pf-unfilled-field');
    }
    return classes.join(' ');
  };

  // ─── Upload Step ─────────────────────────────────────────────────
  // Uses form-field / field-label from CompanyFormOverrides.css (shared)
  const renderUploadStep = () => (
    <div className="modal-body">
      {/* Passport row */}
      {(() => {
        const upload = documentUploads.find(u => u.type === 'passport_front');
        const isUploaded = upload?.uploaded;
        return (
          <div className="form-field" style={{ position: 'relative' }}>
            <label className="field-label">Passport Document</label>
            {isUploaded ? (
              <div className="pf-upload-row">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                <span className="pf-upload-filename">{upload?.file?.name}</span>
                <button type="button" className="pf-upload-remove" onClick={() => { setDocumentUploads(prev => prev.map(u => u.type === 'passport_front' ? { ...u, file: null, uploaded: false, processingStatus: 'pending' as const } : u)); }}>Remove</button>
              </div>
            ) : (
              <div className="pf-upload-row pf-upload-clickable">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="18" rx="2"/><circle cx="12" cy="11" r="3"/><path d="M6 21v-1a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v1"/></svg>
                <span className="pf-upload-action">Click to upload</span>
                <span className="pf-upload-hint">PNG, JPG, PDF</span>
                <input type="file" accept=".png,.jpg,.jpeg,.pdf" className="pf-upload-input"
                  onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFileUpload('passport_front', file); }} />
              </div>
            )}
          </div>
        );
      })()}

      {/* Visa / National ID row */}
      {(() => {
        const upload = documentUploads.find(u => u.type === 'visa_national_id');
        const isUploaded = upload?.uploaded;
        return (
          <div className="form-field" style={{ position: 'relative' }}>
            <label className="field-label">Visa / National ID</label>
            {isUploaded ? (
              <div className="pf-upload-row">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                <span className="pf-upload-filename">{upload?.file?.name}</span>
                <button type="button" className="pf-upload-remove" onClick={() => { setDocumentUploads(prev => prev.map(u => u.type === 'visa_national_id' ? { ...u, file: null, uploaded: false, processingStatus: 'pending' as const } : u)); }}>Remove</button>
              </div>
            ) : (
              <div className="pf-upload-row pf-upload-clickable">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="7" y1="15" x2="10" y2="15"/><line x1="14" y1="15" x2="17" y2="15"/></svg>
                <span className="pf-upload-action">Click to upload</span>
                <span className="pf-upload-hint">PNG, JPG, PDF</span>
                <input type="file" accept=".png,.jpg,.jpeg,.pdf" className="pf-upload-input"
                  onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFileUpload('visa_national_id', file); }} />
              </div>
            )}
          </div>
        );
      })()}

      {/* Profile Photo row */}
      {(() => {
        const upload = documentUploads.find(u => u.type === 'profile_picture');
        const isUploaded = upload?.uploaded;
        return (
          <div className="form-field" style={{ position: 'relative' }}>
            <label className="field-label">Profile Photo</label>
            {isUploaded ? (
              <div className="pf-upload-row">
                {upload?.file && (
                  <img src={URL.createObjectURL(upload.file)} alt="Preview" className="pf-upload-thumb" />
                )}
                <span className="pf-upload-filename">{upload?.file?.name}</span>
                <button type="button" className="pf-upload-remove" onClick={() => { setDocumentUploads(prev => prev.map(u => u.type === 'profile_picture' ? { ...u, file: null, uploaded: false, processingStatus: 'pending' as const } : u)); }}>Remove</button>
              </div>
            ) : (
              <div className="pf-upload-row pf-upload-clickable">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                <span className="pf-upload-action">Click to upload</span>
                <span className="pf-upload-hint">PNG, JPG</span>
                <input type="file" accept=".png,.jpg,.jpeg" className="pf-upload-input"
                  onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFileUpload('profile_picture', file); }} />
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );

  // ─── Form Step ──────────────────────────────────────────────────
  const renderFormStep = () => (
    <form onSubmit={(e) => { e.preventDefault(); savePersonnelRecord(); }} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="modal-body">

        {/* ─── Save Error Banner — pinned at top of form ─── */}
        {saveError && (
          <div style={{
            margin: '0 0 16px 0',
            padding: '12px 16px',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '13px', color: '#991B1B', marginBottom: '2px', fontFamily: "'Segoe UI', sans-serif" }}>
                Save failed
              </div>
              <div style={{ fontSize: '12.5px', color: '#7F1D1D', fontFamily: "'Segoe UI', sans-serif", lineHeight: '1.45' }}>
                {saveError}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSaveError(null)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF',
                padding: '2px', flexShrink: 0, lineHeight: 1,
              }}
              aria-label="Dismiss error"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        )}

        {/* Profile Photo Section */}
        <div className="pf-photo-section">
          <div className="pf-photo-avatar">
            {(() => {
              const profilePicture = documentUploads.find(u => u.type === 'profile_picture' && u.file);
              if (profilePicture?.file) {
                return <img src={URL.createObjectURL(profilePicture.file)} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
              }
              return <div style={{ fontSize: '36px', color: '#9ca3af' }}>👤</div>;
            })()}
          </div>
          
          <div className="pf-photo-controls">
            <input type="file" id="profile-photo-change" accept="image/*"
              onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFileUpload('profile_picture', file); }}
              style={{ display: 'none' }} />
            <label htmlFor="profile-photo-change" className="pf-photo-btn">Change Profile</label>

            {(extractedPhotos.length > 0 || documentUploads.find(u => u.type === 'profile_picture' && u.file)) && (
              <button type="button" className="pf-photo-compare-btn" onClick={() => setShowPhotoComparison(true)}>
                Compare Photos ({extractedPhotos.length + (documentUploads.find(u => u.type === 'profile_picture' && u.file) ? 1 : 0)})
              </button>
            )}
          </div>
        </div>

        {/* ── Cross-document mismatch warnings ── */}
        {crossDocWarnings.length > 0 && !warningsDismissed && (
          <div style={{
            backgroundColor: '#FFF7ED',
            border: '1px solid #FDBA74',
            borderRadius: '8px',
            padding: '14px 16px',
            marginBottom: '20px',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '13px', color: '#9A3412', marginBottom: '8px', fontFamily: "'Segoe UI', sans-serif" }}>
                  Data mismatch between Passport and Visa/ID — please verify
                </div>
                {crossDocWarnings.map((w, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '6px',
                    fontSize: '12.5px',
                    color: '#78350F',
                    marginBottom: idx < crossDocWarnings.length - 1 ? '6px' : '0',
                    fontFamily: "'Segoe UI', sans-serif",
                    lineHeight: '1.4',
                  }}>
                    <span style={{ fontWeight: 600, minWidth: '100px', flexShrink: 0 }}>{w.label}:</span>
                    <span>
                      Passport reads <span style={{ fontWeight: 600, color: '#1E40AF', backgroundColor: '#EFF6FF', padding: '1px 5px', borderRadius: '3px' }}>{w.passportValue}</span>
                      {' '} but Visa/ID reads <span style={{ fontWeight: 600, color: '#9A3412', backgroundColor: '#FFF7ED', padding: '1px 5px', borderRadius: '3px' }}>{w.idValue}</span>
                    </span>
                  </div>
                ))}
                <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '11.5px', color: '#B45309', fontStyle: 'italic' }}>
                    Check the values above and correct any errors before saving.
                  </span>
                  <button
                    type="button"
                    onClick={() => setWarningsDismissed(true)}
                    style={{
                      background: 'none',
                      border: '1px solid #FDBA74',
                      borderRadius: '4px',
                      padding: '3px 10px',
                      fontSize: '11px',
                      color: '#9A3412',
                      cursor: 'pointer',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Document expiry alerts ── */}
        {documentAlerts.length > 0 && (
          <div style={{
            backgroundColor: documentAlerts.some(a => a.severity === 'expired') ? '#FEF2F2' : '#FFFBEB',
            border: `1px solid ${documentAlerts.some(a => a.severity === 'expired') ? '#FECACA' : '#FDE68A'}`,
            borderRadius: '8px',
            padding: '14px 16px',
            marginBottom: '20px',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              {documentAlerts.some(a => a.severity === 'expired') ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              )}
              <div style={{ flex: 1 }}>
                <div style={{
                  fontWeight: 600,
                  fontSize: '13px',
                  color: documentAlerts.some(a => a.severity === 'expired') ? '#991B1B' : '#92400E',
                  marginBottom: '6px',
                  fontFamily: "'Segoe UI', sans-serif",
                }}>
                  {documentAlerts.some(a => a.severity === 'expired')
                    ? 'Expired document detected — manual verification required'
                    : 'Document expiring soon — please verify validity'}
                </div>
                {documentAlerts.map((alert, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '12.5px',
                    color: alert.severity === 'expired' ? '#7F1D1D' : '#78350F',
                    marginBottom: idx < documentAlerts.length - 1 ? '5px' : '0',
                    fontFamily: "'Segoe UI', sans-serif",
                    lineHeight: '1.4',
                  }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '1px 7px',
                      borderRadius: '3px',
                      fontSize: '11px',
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                      backgroundColor: alert.severity === 'expired' ? '#FEE2E2' : alert.severity === 'expiring_soon' ? '#FEF3C7' : '#F0FDF4',
                      color: alert.severity === 'expired' ? '#DC2626' : alert.severity === 'expiring_soon' ? '#D97706' : '#16A34A',
                      border: `1px solid ${alert.severity === 'expired' ? '#FECACA' : alert.severity === 'expiring_soon' ? '#FDE68A' : '#BBF7D0'}`,
                      flexShrink: 0,
                    }}>
                      {alert.severity === 'expired' ? 'EXPIRED' : alert.severity === 'expiring_soon' ? 'EXPIRING' : 'VALID'}
                    </span>
                    <span>{alert.message}</span>
                  </div>
                ))}
                {documentAlerts.some(a => a.severity === 'expired') && (
                  <div style={{ marginTop: '8px', fontSize: '11.5px', color: '#991B1B', fontStyle: 'italic' }}>
                    You can still save this record, but the visitor may need renewed documents for gate pass approval.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Personal Info ── */}
        <h4 className="pf-section-header">Personal Info</h4>
        
        <div className="pf-field-row">
          <div className={`form-field ${validationErrors.firstName ? 'pf-has-error' : ''}`} data-field="firstName">
            <label className="field-label">
              First Name <span className="required">*</span>
              {isExtracted('firstName') && !isUnfilled('firstName') && <span className="pf-extracted-badge">Extracted</span>}
              {isUnfilled('firstName') && <span className="pf-unfilled-badge">Needs input</span>}
            </label>
            <input type="text" value={formData.firstName || ''} onChange={(e) => handleFieldChange('firstName', e.target.value)}
              placeholder="Enter first name" className={fieldClass('firstName')} />
            {validationErrors.firstName && <div className="pf-field-error">{validationErrors.firstName}</div>}
          </div>
          <div className={`form-field ${validationErrors.lastName ? 'pf-has-error' : ''}`} data-field="lastName">
            <label className="field-label">
              Last Name <span className="required">*</span>
              {isExtracted('lastName') && !isUnfilled('lastName') && <span className="pf-extracted-badge">Extracted</span>}
              {isUnfilled('lastName') && <span className="pf-unfilled-badge">Needs input</span>}
            </label>
            <input type="text" value={formData.lastName || ''} onChange={(e) => handleFieldChange('lastName', e.target.value)}
              placeholder="Enter last name" className={fieldClass('lastName')} />
            {validationErrors.lastName && <div className="pf-field-error">{validationErrors.lastName}</div>}
          </div>
        </div>

        <div className="pf-field-row">
          <div className={`form-field ${validationErrors.sex ? 'pf-has-error' : ''}`} data-field="sex">
            <label className="field-label">
              Gender <span className="required">*</span>
              {isExtracted('sex') && !isUnfilled('sex') && <span className="pf-extracted-badge">Extracted</span>}
              {isUnfilled('sex') && <span className="pf-unfilled-badge">Needs input</span>}
            </label>
            <select value={formData.sex || ''} onChange={(e) => handleFieldChange('sex', e.target.value)}
              className={fieldClass('sex', 'field-select')}>
              <option value="">Select...</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
            {validationErrors.sex && <div className="pf-field-error">{validationErrors.sex}</div>}
          </div>
          <div className={`form-field ${validationErrors.placeOfBirth ? 'pf-has-error' : ''}`} data-field="placeOfBirth">
            <label className="field-label">
              Place of Birth <span className="required">*</span>
              {isExtracted('placeOfBirth') && !isUnfilled('placeOfBirth') && <span className="pf-extracted-badge">Extracted</span>}
              {isUnfilled('placeOfBirth') && <span className="pf-unfilled-badge">Needs input</span>}
            </label>
            <input type="text" value={formData.placeOfBirth || ''} onChange={(e) => handleFieldChange('placeOfBirth', e.target.value)}
              placeholder="Enter place of birth" className={fieldClass('placeOfBirth')} />
            {validationErrors.placeOfBirth && <div className="pf-field-error">{validationErrors.placeOfBirth}</div>}
          </div>
        </div>

        <div className="pf-field-row">
          <div className={`form-field ${validationErrors.dateOfBirth ? 'pf-has-error' : ''}`} data-field="dateOfBirth">
            <label className="field-label">
              Date of Birth <span className="required">*</span>
              {isExtracted('dateOfBirth') && !isUnfilled('dateOfBirth') && <span className="pf-extracted-badge">Extracted</span>}
              {isUnfilled('dateOfBirth') && <span className="pf-unfilled-badge">Needs input</span>}
            </label>
            <input type="date" value={formData.dateOfBirth || ''} onChange={(e) => handleFieldChange('dateOfBirth', e.target.value)}
              className={fieldClass('dateOfBirth')} />
            {validationErrors.dateOfBirth && <div className="pf-field-error">{validationErrors.dateOfBirth}</div>}
          </div>
          <div className={`form-field ${validationErrors.nationality ? 'pf-has-error' : ''}`} data-field="nationality">
            <label className="field-label">
              Nationality <span className="required">*</span>
              {isExtracted('nationality') && !isUnfilled('nationality') && <span className="pf-extracted-badge">Extracted</span>}
              {isUnfilled('nationality') && <span className="pf-unfilled-badge">Needs input</span>}
            </label>
            <input type="text" value={formData.nationality || ''} onChange={(e) => handleFieldChange('nationality', e.target.value)}
              placeholder="Enter nationality" className={fieldClass('nationality')} />
            {validationErrors.nationality && <div className="pf-field-error">{validationErrors.nationality}</div>}
          </div>
        </div>

        <div className="form-field">
          <label className="field-label">
            Blood Group
            {isExtracted('bloodGroup') && !isUnfilled('bloodGroup') && <span className="pf-extracted-badge">Extracted</span>}
            {isUnfilled('bloodGroup') && <span className="pf-unfilled-badge">Needs input</span>}
          </label>
          <select value={formData.bloodGroup || ''} onChange={(e) => handleFieldChange('bloodGroup', e.target.value)}
            className={fieldClass('bloodGroup', 'field-select')}>
            <option value="">Select...</option>
            <option value="A+">A+</option><option value="A-">A-</option>
            <option value="B+">B+</option><option value="B-">B-</option>
            <option value="AB+">AB+</option><option value="AB-">AB-</option>
            <option value="O+">O+</option><option value="O-">O-</option>
          </select>
        </div>

        {/* ── Identity Documents ── */}
        <h4 className="pf-section-header" style={{ marginTop: '28px' }}>Identity Documents</h4>
        
        <div className="pf-field-row">
          <div className={`form-field ${validationErrors.nationalIdNo ? 'pf-has-error' : ''}`} data-field="nationalIdNo">
            <label className="field-label">
              National ID Number <span className="required">*</span>
              {isExtracted('nationalIdNo') && !isUnfilled('nationalIdNo') && <span className="pf-extracted-badge">Extracted</span>}
              {isUnfilled('nationalIdNo') && <span className="pf-unfilled-badge">Needs input</span>}
            </label>
            <input type="text" value={formData.nationalIdNo || ''} onChange={(e) => handleFieldChange('nationalIdNo', e.target.value)}
              placeholder="Enter national ID number" className={fieldClass('nationalIdNo')} />
            {validationErrors.nationalIdNo && <div className="pf-field-error">{validationErrors.nationalIdNo}</div>}
          </div>
          <div className={`form-field ${validationErrors.passportNumber ? 'pf-has-error' : ''}`} data-field="passportNumber">
            <label className="field-label">
              Passport Number <span className="required">*</span>
              {isExtracted('passportNumber') && !isUnfilled('passportNumber') && <span className="pf-extracted-badge">Extracted</span>}
              {isUnfilled('passportNumber') && <span className="pf-unfilled-badge">Needs input</span>}
            </label>
            <input type="text" value={formData.passportNumber || ''} onChange={(e) => handleFieldChange('passportNumber', e.target.value)}
              placeholder="Enter passport number" className={fieldClass('passportNumber')} />
            {validationErrors.passportNumber && <div className="pf-field-error">{validationErrors.passportNumber}</div>}
          </div>
        </div>

        <div className="pf-field-row">
          <div className={`form-field ${validationErrors.passportIssueDate ? 'pf-has-error' : ''}`} data-field="passportIssueDate">
            <label className="field-label">
              Passport Issue Date <span className="required">*</span>
              {isExtracted('passportIssueDate') && !isUnfilled('passportIssueDate') && <span className="pf-extracted-badge">Extracted</span>}
              {isUnfilled('passportIssueDate') && <span className="pf-unfilled-badge">Needs input</span>}
            </label>
            <input type="date" value={formData.passportIssueDate || ''} onChange={(e) => handleFieldChange('passportIssueDate', e.target.value)}
              className={fieldClass('passportIssueDate')} />
            {validationErrors.passportIssueDate && <div className="pf-field-error">{validationErrors.passportIssueDate}</div>}
          </div>
          <div className={`form-field ${validationErrors.passportExpiryDate ? 'pf-has-error' : ''}`} data-field="passportExpiryDate">
            <label className="field-label">
              Passport Expiry Date <span className="required">*</span>
              {isExtracted('passportExpiryDate') && !isUnfilled('passportExpiryDate') && <span className="pf-extracted-badge">Extracted</span>}
              {isUnfilled('passportExpiryDate') && <span className="pf-unfilled-badge">Needs input</span>}
              {(() => {
                const alert = documentAlerts.find(a => a.field === 'passportExpiryDate');
                if (!alert) return null;
                const bgColor = alert.severity === 'expired' ? '#FEE2E2' : alert.severity === 'expiring_soon' ? '#FEF3C7' : '#F0FDF4';
                const textColor = alert.severity === 'expired' ? '#DC2626' : alert.severity === 'expiring_soon' ? '#D97706' : '#16A34A';
                const borderColor = alert.severity === 'expired' ? '#FECACA' : alert.severity === 'expiring_soon' ? '#FDE68A' : '#BBF7D0';
                const text = alert.severity === 'expired' ? 'Expired' : alert.severity === 'expiring_soon' ? `${alert.daysRemaining}d left` : `${alert.daysRemaining}d left`;
                return <span style={{ marginLeft: '6px', padding: '1px 6px', borderRadius: '3px', fontSize: '10px', fontWeight: 700, backgroundColor: bgColor, color: textColor, border: `1px solid ${borderColor}` }}>{text}</span>;
              })()}
            </label>
            <input type="date" value={formData.passportExpiryDate || ''} onChange={(e) => handleFieldChange('passportExpiryDate', e.target.value)}
              className={fieldClass('passportExpiryDate')}
              style={documentAlerts.find(a => a.field === 'passportExpiryDate' && a.severity === 'expired') ? { borderBottomColor: '#EF4444' } : undefined} />
            {validationErrors.passportExpiryDate && <div className="pf-field-error">{validationErrors.passportExpiryDate}</div>}
          </div>
        </div>

        {/* ── Visa Info ── */}
        <h4 className="pf-section-header" style={{ marginTop: '28px' }}>Visa Info</h4>
        
        <div className="pf-field-row">
          <div className={`form-field ${validationErrors.typeOfVisa ? 'pf-has-error' : ''}`} data-field="typeOfVisa">
            <label className="field-label">
              Visa Type <span className="required">*</span>
              {isExtracted('typeOfVisa') && !isUnfilled('typeOfVisa') && <span className="pf-extracted-badge">Extracted</span>}
              {isUnfilled('typeOfVisa') && <span className="pf-unfilled-badge">Needs input</span>}
            </label>
            <select value={formData.typeOfVisa || ''} onChange={(e) => handleFieldChange('typeOfVisa', e.target.value)}
              className={fieldClass('typeOfVisa', 'field-select')}>
              <option value="">Select...</option>
              <option value="Tourist">Tourist</option><option value="Business">Business</option>
              <option value="Work">Work</option><option value="Student">Student</option>
              <option value="Transit">Transit</option><option value="Resident">Resident</option>
              <option value="Other">Other</option>
            </select>
            {validationErrors.typeOfVisa && <div className="pf-field-error">{validationErrors.typeOfVisa}</div>}
          </div>
          <div className={`form-field ${validationErrors.visaIssueDate ? 'pf-has-error' : ''}`} data-field="visaIssueDate">
            <label className="field-label">
              Visa Issue Date <span className="required">*</span>
              {isExtracted('visaIssueDate') && !isUnfilled('visaIssueDate') && <span className="pf-extracted-badge">Extracted</span>}
              {isUnfilled('visaIssueDate') && <span className="pf-unfilled-badge">Needs input</span>}
            </label>
            <input type="date" value={formData.visaIssueDate || ''} onChange={(e) => handleFieldChange('visaIssueDate', e.target.value)}
              className={fieldClass('visaIssueDate')} />
            {validationErrors.visaIssueDate && <div className="pf-field-error">{validationErrors.visaIssueDate}</div>}
          </div>
        </div>

        <div className={`form-field ${validationErrors.visaExpiry ? 'pf-has-error' : ''}`} data-field="visaExpiry">
          <label className="field-label">
            Visa Expiry Date <span className="required">*</span>
            {isExtracted('visaExpiry') && !isUnfilled('visaExpiry') && <span className="pf-extracted-badge">Extracted</span>}
            {isUnfilled('visaExpiry') && <span className="pf-unfilled-badge">Needs input</span>}
            {(() => {
              const alert = documentAlerts.find(a => a.field === 'visaExpiry');
              if (!alert) return null;
              const bgColor = alert.severity === 'expired' ? '#FEE2E2' : alert.severity === 'expiring_soon' ? '#FEF3C7' : '#F0FDF4';
              const textColor = alert.severity === 'expired' ? '#DC2626' : alert.severity === 'expiring_soon' ? '#D97706' : '#16A34A';
              const borderColor = alert.severity === 'expired' ? '#FECACA' : alert.severity === 'expiring_soon' ? '#FDE68A' : '#BBF7D0';
              const text = alert.severity === 'expired' ? 'Expired' : alert.severity === 'expiring_soon' ? `${alert.daysRemaining}d left` : `${alert.daysRemaining}d left`;
              return <span style={{ marginLeft: '6px', padding: '1px 6px', borderRadius: '3px', fontSize: '10px', fontWeight: 700, backgroundColor: bgColor, color: textColor, border: `1px solid ${borderColor}` }}>{text}</span>;
            })()}
          </label>
          <input type="date" value={formData.visaExpiry || ''} onChange={(e) => handleFieldChange('visaExpiry', e.target.value)}
            className={fieldClass('visaExpiry')}
            style={documentAlerts.find(a => a.field === 'visaExpiry' && a.severity === 'expired') ? { borderBottomColor: '#EF4444' } : undefined} />
          {validationErrors.visaExpiry && <div className="pf-field-error">{validationErrors.visaExpiry}</div>}
        </div>
      </div>

      {/* Footer */}
      <div className="modal-footer">
        <button type="button" className="btn-cancel"
          onClick={() => { updateStep('upload'); setFormData({}); setCombinedData(null); setValidationErrors({}); setExtractedPhotos([]); setCrossDocWarnings([]); setWarningsDismissed(false); setSaveError(null); setDocumentAlerts([]); setGenuinelyExtractedFields(new Set()); }}>
          Re-upload
        </button>
        <button type="button" className="btn-cancel" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn-submit">Save Personnel Record</button>
      </div>
    </form>
  );

  // Processing and saving steps
  const renderProcessingStep = () => (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      width: '100%', height: '100%', padding: '48px 32px', textAlign: 'center',
    }}>
      <div style={{
        width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#FFF4F1',
        display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px',
      }}>
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#F3694A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13" opacity="0.4"/>
          <line x1="16" y1="17" x2="8" y2="17" opacity="0.4"/>
        </svg>
      </div>
      <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
      <h3 style={{
        fontSize: '17px', fontWeight: 600, color: '#1F2937', margin: '0 0 6px',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}>Analysing Documents</h3>
      <p style={{
        fontSize: '13.5px', color: '#6B7280', margin: '0', maxWidth: '320px', lineHeight: '1.5',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}>AI is reading your passport and ID to extract personal details. This usually takes a few seconds.</p>
    </div>
  );

  const renderSavingStep = () => (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      width: '100%', height: '100%', padding: '48px 32px', textAlign: 'center',
    }}>
      <div style={{
        width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#F0F9FF',
        display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px',
      }}>
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
          <polyline points="15 13 12 10 9 13"/>
          <line x1="12" y1="10" x2="12" y2="17"/>
        </svg>
      </div>
      <div className="spinner" style={{ margin: '0 auto 20px', borderTopColor: '#3B82F6' }}></div>
      <h3 style={{
        fontSize: '17px', fontWeight: 600, color: '#1F2937', margin: '0 0 6px',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}>Saving Personnel Record</h3>
      <p style={{
        fontSize: '13.5px', color: '#6B7280', margin: '0', maxWidth: '320px', lineHeight: '1.5',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}>Creating the visitor profile and securely uploading documents to cloud storage.</p>
    </div>
  );

  // ─── Render ─────────────────────────────────────────────────────
  // This component sits INSIDE PersonnelManagement's modal-content-with-help.
  // It must NOT add its own help panel, toggle strip, or modal wrapper.

  if (step === 'upload') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
          {renderUploadStep()}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn-submit" onClick={processAllDocuments}
            disabled={isProcessing || getUploadedCount() === 0}>
            {isProcessing ? 'Processing...' : 'Process Documents'}
          </button>
        </div>
        <PhotoComparisonModal />
      </div>
    );
  }

  if (step === 'processing') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flex: 1 }}>
        {renderProcessingStep()}
      </div>
    );
  }

  if (step === 'form') {
    return (
      <>
        {renderFormStep()}
        <PhotoComparisonModal />
      </>
    );
  }

  if (step === 'saving') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flex: 1 }}>
        {renderSavingStep()}
      </div>
    );
  }

  return null;
};

export default EnhancedPersonnelCreation;